import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "./globalStyles/fonts/vfs_fonts.js";
import styles from "./globalStyles/styles";
import ky from "ky-universal";
import { BORDER_COLOR, COL_ONE_WIDTH } from "./utility/pdfConstants.js";
import { cleanText, styleElement, toTitleCase } from "./utility/pdfUtilityFunctions.js";
import { createImageComponentDocDef } from "./component-definitions/createImageComponentDocDef.js";

pdfMake.vfs = vfs;

pdfMake.fonts = {
	Avenir: {
		normal: "AvenirLTPro-Light.ttf",
		bold: "AvenirLTPro-Heavy.ttf",
		italics: "AvenirLTPro-LightOblique.ttf",
		bolditalics: "AvenirLTPro-Oblique.ttf"
	},
	Sentinel: {
		normal: "Sentinel-Light.otf",
		bold: "Sentinel-Semibold.otf",
		italics: "Sentinel-LightItal.otf",
		bolditalics: "Sentinel-SemiboldItal.otf"
	},
	AvenirNext: {
		normal: "AvenirNextLTPro-Cn.ttf",
		bold: "AvenirNextLTPro-Cn.ttf",
		italics: "AvenirNextLTPro-Cn.ttf",
		bolditalics: "AvenirNextLTPro-Cn.ttf"
	}
};

const walkContentNodes = (el, images) => {
	let contentNode = [],
		metaNode = [],
		authorNodes = [],
		exhibitLabels = [],
		exhibitTitles = [],
		exhibitSubTitles = [],
		exhibitDescriptions = [],
		rejectTags = ["BUTTON"],
		// textTags = ["P"],
		treeWalker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT, {
			acceptNode: (node) => {
				if (
					rejectTags.includes(node.tagName) ||
					node.classList.contains("pdf--ignore") ||
					// ignore image and exhibit info nodes so they can be added manually
					node.classList.contains("content-page-module-exhibit-title-text") ||
					node.classList.contains("content-page-module-exhibit-full-title-text") ||
					node.classList.contains("pdf--image-title") ||
					node.classList.contains("content-page-module-exhibit-title-label") ||
					node.classList.contains("content-page-module-exhibit-full-title-label") ||
					node.classList.contains("pdf--exhibit-subTitle") ||
					node.classList.contains("pdf--exhibit-full-override") ||
					node.classList.contains("pdf--exhibit-desc") ||
					node.classList.contains("content-page-module-exhibit-full-desc") ||
					node.classList.contains("pdf--image-description")
				) {
					return NodeFilter.FILTER_REJECT;
				} else {
					return NodeFilter.FILTER_ACCEPT;
				}
			}
		}),
		node;

	let firstItemInBody = true,
		//set variable for sections of H2/P to know that there are authors and top margin is not needed
		authorsEl = document.getElementsByClassName("pdf--byLine")[0],
		areThereAuthors = false,
		isDisclosure = false,
		isFootnote = false,
		isInfoPopup = false,
		isFullExhibit = false,
		isExhibit = false,
		isAccordion = false,
		isKeytakeaway = false,
		isUl = false,
		isLi = false,
		isImageComponent = false,
		isNonBreaking = false,
		isPageBreak = false;

	const logo = new XMLSerializer().serializeToString(document.querySelector(".image-logo")),
		footerStandardDisclosure = "Please see the end of this document for important disclosures.",
		dfaTitle = "Dimensional Fund Advisors",
		homeUrl = "dimensional.com",
		pageUrl = window.location.href,
		// shorten xlink urls
		pageUrlText = pageUrl.includes("xlink") ? pageUrl.split("xlink")[0] + "xlink" : pageUrl,
		todaysDate = new Date().toLocaleString("en-US", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric"
		});

	if (typeof authorsEl !== "undefined") {
		areThereAuthors = authorsEl.innerText !== "" ? true : false;
	}

	while ((node = treeWalker.nextNode())) {
		if (node.closest(".pdf--ignore") === null) {
			isFootnote = Boolean(node.closest(".content-page-footnotes"));
			isDisclosure = Boolean(node.closest(".content-page-disclaimer"));
			isInfoPopup = Boolean(node.closest(".info-popup-content"));
			isFullExhibit = Boolean(node.closest(".content-page-module-exhibit-full"));
			isExhibit = Boolean(node.closest(".content-page-module-exhibit"));
			isImageComponent = Boolean(node.closest(".content-page-module-image"));
			isAccordion = Boolean(
				node.closest(".content-page-accordion-dropdown-title") ||
					node.closest(".content-page-accordion-dropdown-description") ||
					node.closest(".content-page-accordion-dropdown-item-title")
			);
			isKeytakeaway = Boolean(node.closest(".content-page-key-takeaway"));
			isUl = Boolean(node.closest("UL"));
			isLi = Boolean(node.closest("LI"));
			isNonBreaking = Boolean(node.closest(".pdf--non-breaking"));
			isPageBreak = Boolean(node.closest(".pdf--page-break"));

			//this is for pages that have 'STRATEGY SPOTLIGHT', 'ADVISOR TALKING POINTS', etc. before the page title
			if (node.classList.contains("pdf--topic")) {
				contentNode.push({
					margin: [0, 40, 0, 7.24],
					text: node.textContent.toUpperCase(),
					style: "article-tag"
				});
			}

			// main page title section
			else if (node.classList.contains("pdf--title")) {
				//teal eyebrow
				contentNode.push({
					margin: [0, 38, 0, 8.24],
					canvas: [
						{
							type: "rect",
							x: 0,
							y: 0,
							w: 24.29,
							h: 2.34,
							color: "#00b7bd"
						}
					]
				});
				//title
				contentNode.push({
					style: "h1",
					text: node.textContent,
					margin: [0, 0, 0, 35]
				});
			}

			// meta data
			if (node.classList.contains("pdf--date")) {
				metaNode.push({
					text: node.textContent,
					style: "meta"
				});
			}

			// no bylines
			else if (node.classList.contains("pdf--byLine") && node.textContent === "") {
				contentNode.push({
					text: metaNode
				});
			}

			// bylines split into columns
			else if (node.classList.contains("pdf--byLine") && node.textContent !== "") {
				const allAuthors = document.getElementsByClassName("pdf--author"),
					authorsCount = allAuthors !== "undefined" ? allAuthors.length : 0,
					rowCount = Math.ceil(authorsCount / 2);
				let row = [];
				// single author
				if (authorsCount === 1) {
					const author = allAuthors[0].textContent;
					const title = allAuthors[0].nextSibling.textContent.trim();
					const node = [
						{
							stack: [
								{ text: author, style: "byline" },
								title ? { text: title, style: "byline" } : undefined,
								{ text: "\n" }
							]
						},
						""
					]; //last column is empty if there is an odd # of authors
					authorNodes.push(node);
				} else if (authorsCount > 1) {
					// multiple authors
					for (let i = 0; i < authorsCount; i++) {
						if (allAuthors[i].nodeName === "A") {
							const author = allAuthors[i].textContent;
							let title = allAuthors[i].nextSibling.textContent.trim();
							if (title) title = title.split("|")[0].trim();

							// uneven count and the last author
							if (i % 2 === 0 && i > 0 && authorsCount % 2 === 1 && i === authorsCount - 1) {
								const node = [
									{
										stack: [
											{ text: author, style: "byline" },
											title ? { text: title, style: "byline" } : undefined,
											{ text: "\n" }
										]
									},
									""
								]; //last column is empty if there is an odd # of authors
								authorNodes.push(node);
							}
							// all other authors
							else {
								row.push({
									stack: [
										{ text: author, style: "byline" },
										title ? { text: title, style: "byline" } : undefined,
										{ text: "\n" }
									]
								});
								// start new row if even number
								if ((i + 1) % 2 === 0 && i > 0) {
									authorNodes.push(row);
									row = [];
								}
							}
						}
					}
				}
				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							style: "meta",
							margin: [0, authorsCount > 2 ? rowCount * 47 : 48, 25, 0],
							stack: metaNode
						},
						[
							{
								margin: [0, 0, 0, 30],
								table: {
									widths: [180, 180],
									body: authorNodes
								},
								layout: "noBorders"
							}
						]
					]
				});
			}

			//keyTakeaways styling for PDF
			else if (node.classList.contains("content-page-key-takeaway")) {
				let listItems = [];
				let listLIs = node.querySelectorAll("li");

				for (let i = 0; i < listLIs.length; i++) {
					const itemText = styleElement(listLIs[i], "default", false, null, true);
					if (i === listLIs.length - 1) {
						// last key takeaway item has negative margin to limit length of left border
						listItems.push({
							margin: [5, 5, 5, -20],
							text: itemText
						});
					} else {
						listItems.push({
							margin: [5, 5, 5, 0],
							text: itemText
						});
					}
				}

				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [0, 0, 0, 65],
							table: {
								body: [
									[
										{
											//border left true for vertical line on keyTakeaway
											border: [true, false, false, false],
											stack: [
												{
													margin: [5, 0, 0, 5],
													text: styleElement(
														node.querySelector(".content-page-key-takeaway-title"),
														"keyTakeaway",
														false,
														null,
														true
													)
												},
												{
													margin: [1, 0, 0, 0],
													type: "square",
													markerColor: "#c4b000",
													ul: listItems
												}
											]
										}
									]
								]
							},
							layout: {
								defaultBorder: false,
								vLineColor: function () {
									return BORDER_COLOR;
								}
							}
						}
					]
				});
			}

			// Shareable quote
			else if (node.classList.contains("content-page-shareable-quote")) {
				contentNode.push({
					unbreakable: true,
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							stack: [
								{
									canvas: [
										{
											type: "line",
											x1: 0,
											y1: 0,
											x2: 32,
											y2: 0,
											lineWidth: 3.7,
											lineColor: "#c4b000"
										}
									],
									margin: [0, 18, 0, 6]
								},
								{
									text: node.querySelector(".content-page-shareable-quote-text").innerText,
									style: "shareableQuote",
									margin: [0, 6, 0, 18]
								}
							]
						}
					]
				});
			} else if (isAccordion) {
				// accordion title
				if (node.classList.contains("content-page-accordion-dropdown-title")) {
					contentNode.push({
						columns: [
							{
								width: COL_ONE_WIDTH,
								text: ""
							},
							{
								margin: [
									0,
									firstItemInBody && areThereAuthors ? -28 : firstItemInBody ? -9 : 0,
									0,
									10
								],
								width: "*",
								text: node.innerText.toUpperCase(),
								style: "subhead-1"
							}
						]
					});
					firstItemInBody = false;
				}

				// accordion descriptions
				else if (node.classList.contains("content-page-accordion-dropdown-description")) {
					contentNode.push({
						columns: [
							{
								width: COL_ONE_WIDTH,
								text: ""
							},
							{
								margin: [0, 0, 0, 10],
								width: "*",
								text: styleElement(node, "default", false)
							}
						]
					});
				}

				// accordion items
				else if (node.classList.contains("content-page-accordion-dropdown-item-title")) {
					// if accordion item contains a pdf--ignore item then don't show the whole item

					contentNode.push({
						columns: [
							{
								width: COL_ONE_WIDTH,
								text: ""
							},
							{
								margin: [0, 5, 0, 10],
								width: "*",
								text: styleElement(node, "exhibit-label", false)
							}
						]
					});
				}
			}

			// external link component
			else if (node.classList.contains("content-page-external-link")) {
				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [0, 0, 0, 15],
							width: "*",
							columns: [
								{
									text: styleElement(node, "externalLink", false),
									width: "auto"
								},
								{
									svg: new XMLSerializer().serializeToString(node.querySelector(".image-link-out")),
									margin: [5, 0, 0, 0]
								}
							]
						}
					]
				});
			}

			// footnotes
			else if (node.classList.contains("content-page-footnotes")) {
				let footnoteNodes = [];
				let StyleFootnotes = [];
				// line above footnotes
				footnoteNodes.push({
					canvas: [
						{ type: "line", x1: 0, y1: 0, x2: 170, y2: 0, lineWidth: 1, lineColor: BORDER_COLOR }
					],
					margin: [0, 20, 0, 10]
				});
				// if window.footnotes exists then we have hidden footnotes
				if (typeof window.footnotes !== "undefined") {
					footnoteNodes.push({
						stack: styleHiddenFootnotes(window.footnotes.footnotes)
					});

					// otherwise they are standard html
				} else {
					let currentNodeLIs = node.querySelectorAll("LI");

					for (let i = 0; i < currentNodeLIs.length; i++) {
						StyleFootnotes.push(styleElement(currentNodeLIs[i], "footNote", true));
					}

					for (let j = 0; j < currentNodeLIs.length; j++) {
						footnoteNodes.push({
							columns: [
								{
									width: "*",
									text: StyleFootnotes[j]
								}
							]
						});
					}
				}
				contentNode.push({
					unbreakable: true,
					stack: footnoteNodes
				});
			}

			// end of page disclosures
			// there can be more than one disclosure element
			else if (
				node.classList.contains("content-page-disclaimer") &&
				// exclude exhibit disclosures from body
				node.closest(".content-page-module-exhibit-disclosures-top") === null &&
				node.closest(".content-page-module-exhibit-disclosures-bottom") === null
			) {
				let discType = node.dataset.containerType;
				let discTitle = node.querySelector("p").textContent;
				let isRealDisclosure = Boolean(discType === "disclosure");
				let disclosureType = [];

				// line above disclosures
				if (isRealDisclosure) {
					disclosureType.push({
						canvas: [
							{ type: "line", x1: 0, y1: 0, x2: 498, y2: 0, lineWidth: 1, lineColor: BORDER_COLOR }
						],
						margin: [0, 20, 0, 4]
					});
				}

				// if text is not "disclosures" then write out the disclosure element title
				if (!isRealDisclosure) {
					disclosureType.push({
						text: discTitle.toUpperCase(),
						style: "appendixTitle"
					});
				}
				// non hidden disclosures
				let discNodes = node.querySelector(".content-page-disclosures-content");

				for (let i = 0; i < discNodes.children.length; i++) {
					let thisStyle = "disclosure";

					if (!isRealDisclosure) {
						thisStyle = "appendix";
					}

					let styledEl = styleElement(discNodes.children[i], thisStyle, false);
					if (i === 0) {
						contentNode.push({
							stack: [disclosureType]
						});
					}
					contentNode.push({
						stack: [styledEl]
					});
				}
			}

			//for the first page and if the article starts with an H2 instead of a paragraph, if there are no authors a margin must be added to the top to align with the date in the first column
			else if (node.tagName === "H2") {
				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [0, firstItemInBody && areThereAuthors ? -17 : 3, 0, 6],
							width: "*",
							text: node.textContent.toUpperCase(),
							style: "subhead-1"
						}
					]
				});
				firstItemInBody = false;
			}

			// for image tags NOT including image component
			else if (
				node.tagName === "IMG" &&
				node.parentElement.tagName !== "PICTURE" &&
				!node.closest(".content-page-module-image")
			) {
				// Search within parent with same dataId for nodes that match label, title, subtitle, description elements and assign to img
				const imageParent = node.parentElement.closest("[data-pdf-id]");
				const imageSiblings = Array.from(imageParent.getElementsByTagName("*"));
				const pdfId = node.dataset.pdfId;
				imageSiblings.forEach((imageSibling) => {
					// for exhibit Main titles
					if (
						imageSibling.classList.contains("content-page-module-exhibit-title-text") ||
						imageSibling.classList.contains("content-page-module-exhibit-full-title-text")
					) {
						exhibitTitles.push({
							id: imageSibling.closest("[data-pdf-id]").getAttribute("data-pdf-id"),
							title: cleanText(imageSibling.innerText)
						});
					}
					// //for exhibit labels 'Exhibit 1', 'Exhibit 2'
					if (
						imageSibling.classList.contains("content-page-module-exhibit-title-label") ||
						imageSibling.classList.contains("content-page-module-exhibit-full-title-label")
					) {
						exhibitLabels.push({
							id: imageSibling.closest("[data-pdf-id]").getAttribute("data-pdf-id"),
							label: imageSibling.innerText
						});
					}
					//for exhibits that have subtitles
					if (
						imageSibling.classList.contains("pdf--exhibit-subTitle") ||
						imageSibling.classList.contains("pdf--exhibit-full-override")
					) {
						exhibitSubTitles.push({
							id: imageSibling.closest("[data-pdf-id]").getAttribute("data-pdf-id"),
							subTitle: cleanText(imageSibling.innerText)
						});
					}
					//for exhibits that have description
					if (
						imageSibling.classList.contains("pdf--exhibit-desc") ||
						imageSibling.classList.contains("content-page-module-exhibit-full-desc")
					) {
						exhibitDescriptions.push({
							id: imageSibling.closest("[data-pdf-id]").getAttribute("data-pdf-id"),
							desc: cleanText(imageSibling.innerText)
						});
					}
				});
				// match images, image labels, image titles and subtitles using their data-pdf-id
				const filterFn = (el) => parseInt(el.id, 10) === parseInt(pdfId, 10);

				let img = images.filter(filterFn),
					exhibitLabel = exhibitLabels.filter(filterFn),
					exhibitTitle = exhibitTitles.filter(filterFn),
					exhibitSubTitle = exhibitSubTitles.filter(filterFn),
					exhibitDesc = exhibitDescriptions.filter(filterFn),
					hasTopBorder = false,
					hasBottomBorder = false,
					topBorderWidth = 0,
					bottomBorderWidth = 0,
					imageTopMargin = 15;
				const exhibitInfo = [];

				if (isExhibit) {
					let imageContainer = node.closest(".content-page-module-image"),
						exhibitContainer = node.closest(".content-page-module-exhibit");

					if (exhibitLabel.length > 0)
						exhibitInfo.push({
							text: toTitleCase(exhibitLabel[0].label),
							style: "exhibit-label",
							margin: [0, 0, 0, 2.5]
						});
					if (exhibitTitle.length > 0)
						exhibitInfo.push({ text: exhibitTitle[0].title, style: "exhibit-title" });
					if (exhibitSubTitle.length > 0)
						exhibitInfo.push({
							text: exhibitSubTitle[0].subTitle,
							style: "exhibit-subtitle",
							margin: [0, 1.5, 0, 0]
						});
					if (exhibitDesc.length > 0)
						exhibitInfo.push({
							text: exhibitDesc[0].desc,
							style: "exhibit-desc",
							margin: [0, 1.5, 0, 0]
						});
					// top and bottom borders for images or exhibit if set in cms
					if (
						node.closest(".content-page-module-image--border-top") !== null ||
						node.closest(".content-page-module-exhibit--border-top") !== null
					) {
						hasTopBorder = true;
					}
					if (imageContainer !== null) {
						if (
							imageContainer.querySelector(".content-page-module-image--border-bottom") !== null
						) {
							hasBottomBorder = true;
						}
					}
					if (exhibitContainer !== null) {
						if (
							exhibitContainer.querySelector(".content-page-module-exhibit--border-bottom") !== null
						) {
							hasBottomBorder = true;
						}
					}
				}

				// set margin above if a single exhibit or an image with a border set
				if (hasTopBorder) {
					topBorderWidth = 400;
				}

				// set margin below if element is part of a set of stacked exhibits and isn't the last one or element is an image with a border set below
				if (hasBottomBorder) {
					bottomBorderWidth = 400;
				}

				// if image with no top border then adjust margin
				if (topBorderWidth === 0) {
					imageTopMargin = 0;
				}

				// if there's no border and the image is the first item in the body adjust margin to line up with the date
				if (topBorderWidth === 0 && firstItemInBody) {
					if (areThereAuthors) {
						imageTopMargin = -28;
					} else {
						imageTopMargin = -9;
					}
					firstItemInBody = false;
				}

				if (img.length > 0) {
					let imageNodeContent = {
						unbreakable: true,
						stack: [
							// top border and disclosures
							{
								columns: [
									{
										width: COL_ONE_WIDTH,
										stack: []
									},
									{
										width: "*",
										stack: [
											// top border line
											{
												canvas: [
													{
														type: "line",
														x1: 0,
														y1: 0,
														x2: topBorderWidth,
														y2: 0,
														lineWidth: 1,
														lineColor: BORDER_COLOR
													}
												],
												margin: [0, topBorderWidth ? 20 : 0, 0, 0]
											},
											// top exhibit disclosure container
											{
												text: img[0].disclosuresTop.length > 0 ? img[0].disclosuresTop : [],
												margin: [
													0,
													img[0].disclosuresTop.length > 0 ? 20 : 0,
													0,
													img[0].disclosuresTop.length > 0 ? (imageTopMargin === 15 ? 15 : 30) : 0
												]
											}
										]
									}
								]
							},
							// image top margin
							{
								text: [],
								margin: [
									0,
									imageTopMargin, // 15 if top border exists
									0,
									topBorderWidth === 0 && isExhibit === false ? 15 : 0
								]
							},
							// image element and disclosures
							{
								columns: [
									{
										width: COL_ONE_WIDTH,
										margin: [0, 0, 10, 0],
										stack: exhibitInfo
									},
									{
										width: "*",
										stack: [
											{
												image: img[0].pngDataUrl,
												fit: [img[0].fitWidth, img[0].fitHeight]
											},
											// bottom exhibit disclosure container
											{
												text: img[0].disclosuresBottom.length > 0 ? img[0].disclosuresBottom : [],
												margin: [0, 32, 0, img[0].disclosuresBottom.length > 0 ? 14 : 0]
											},
											// legacy disclosures
											{
												text: img[0].disclosure.length > 0 ? img[0].disclosure : [],
												margin: [
													0,
													img[0].disclosure.length > 0 ? 10 : 0,
													0,
													img[0].disclosure.length > 0 ? 7 : 0
												]
											},
											// bottom border line
											{
												canvas: [
													{
														type: "line",
														x1: 0,
														y1: 0,
														x2: bottomBorderWidth,
														y2: 0,
														lineWidth: 1,
														lineColor: BORDER_COLOR
													}
												],
												margin: [0, bottomBorderWidth ? 15 : 0, 0, bottomBorderWidth ? 20 : 0]
											},
											// if no bottom border, add bottom margin to separate this from other page content
											{
												text: [],
												margin: [0, 0, 0, bottomBorderWidth === 0 ? 20 : 0]
											}
										]
									}
								]
							}
						]
					};
					contentNode.push(imageNodeContent);
				}
			}

			// image component
			else if (node.classList.contains("content-page-module-image")) {
				const id = node.querySelector("img").getAttribute("data-pdf-id");
				const image = images.find((i) => i.id === id);
				const imageComponentDefinition = createImageComponentDocDef(image, node, areThereAuthors);
				contentNode.push(imageComponentDefinition);
			}

			// body text.  Sometimes it is wrapped in an rte sometimes it is just plain text
			else if (node.classList.contains("body-1-left-almost-black") && !isAccordion) {
				let isRte = node.querySelector(".rtf-container") !== null ? true : false;
				if (!isRte) {
					let styledEl = styleElement(node, "default", false);
					if (styledEl.length > 0) {
						contentNode.push({
							columns: [
								{
									width: COL_ONE_WIDTH,
									text: ""
								},
								{
									width: "*",
									text: styledEl,
									margin: [
										0,
										firstItemInBody && areThereAuthors ? -18 : firstItemInBody ? -10 : 0,
										0,
										0
									]
								}
							]
						});
						firstItemInBody = false;
					}
				}
			} else if (node.classList.contains("pdf--non-breaking")) {
				/* special formatting elements (non breaking elements) */
				let nonBreakingEls = [];
				isNonBreaking = true;

				for (let i = 0; i < node.children.length; i++) {
					nonBreakingEls.push({
						unbreakable: true,
						text: styleElement(node.children[i], "default", false)
					});
				}

				contentNode.push({
					unbreakable: true,
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [
								0,
								firstItemInBody && areThereAuthors ? -28 : firstItemInBody ? -9 : 0,
								0,
								0
							],
							width: "*",
							text: nonBreakingEls,
							unbreakable: true
						}
					]
				});
				firstItemInBody = false;
			} else if (node.classList.contains("pdf--page-break")) {
				/* special formatting elements (page break) */
				isPageBreak = true;
				contentNode.push({
					text: "",
					pageBreak: "before"
				});
			}

			// Generic content falls through to here
			//this is for paragraphs, for the first page and first paragraph if there are no authors a margin must be added to the top to align with the date in the first column
			else if (
				node.tagName === "P" &&
				!node.classList.contains("pdf--date") &&
				!node.classList.contains("pdf--byLine") &&
				!isInfoPopup &&
				!isDisclosure &&
				!isFootnote &&
				!isFullExhibit &&
				!isExhibit &&
				!isImageComponent &&
				!isUl &&
				!isLi &&
				!isAccordion &&
				!isNonBreaking &&
				!isPageBreak
			) {
				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [
								0,
								firstItemInBody && areThereAuthors ? -28 : firstItemInBody ? -9 : 0,
								0,
								0
							],
							width: "*",
							text: styleElement(node, "default", false)
						}
					]
				});
				firstItemInBody = false;
			} else if (
				(node.tagName === "UL" || node.tagName === "OL") &&
				!isInfoPopup &&
				!isDisclosure &&
				!isFootnote &&
				!isFullExhibit &&
				!isExhibit &&
				!isImageComponent &&
				!isAccordion &&
				!isKeytakeaway
			) {
				let listIls = node.querySelectorAll("li"),
					listItems = [];

				for (let i = 0; i < listIls.length; i++) {
					listItems.push({
						margin: [0, 0, 0, 5],
						text: styleElement(listIls[i], "default", false)
					});
				}

				contentNode.push({
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [0, 0, 0, 10],
							width: "*",
							ul: listItems
						}
					]
				});
			}
		}
	}

	/* forced blank space at bottom of last page content */
	contentNode.push({
		stack: [
			{
				canvas: [
					{
						type: "rect",
						x: 0,
						y: 0,
						w: 400,
						h: 70,
						lineColor: "#fff"
					}
				]
			}
		]
	});

	// content for pdfmake to render
	const dd = {
		pageMargins: [50, 80, 43, 90],
		header: function (currentPage) {
			// first page only
			if (currentPage === 1) {
				let usage = document.querySelector(".content-page-metadata").dataset.pdfusage;
				let notBoldHeader =
					document.querySelector(".content-page-metadata").dataset.pdfusageHighlight;
				let styleHeader = "usage";
				if (notBoldHeader === "false") {
					styleHeader = "usage-header-not-bold";
				}
				return {
					columns: [
						{
							width: "auto",
							margin: [50, 43, 0, 0],
							text:
								usage !== undefined && usage !== ""
									? decodeURIComponent(usage.toUpperCase().replace(/\+/g, "%20"))
									: "",
							style: styleHeader
						},
						{
							alignment: "right",
							margin: [0, 43, 27, 0],
							svg: logo,
							fit: [116, 27]
						}
					]
				};
			} else {
				// all other pages
				return {
					columns: [
						{
							width: "auto",
							margin: [25, 25, 25, 25],
							text: currentPage,
							style: "meta"
						},
						{
							width: "*",
							alignment: "right",
							margin: [25, 25, 50, 25],
							stack: [dfaTitle, { text: footerStandardDisclosure, style: "footer" }],
							style: "meta"
						}
					]
				};
			}
		},
		content: contentNode,
		footer: function (currentPage, pageCount) {
			// render footer disclosure at bottom of first page.  It's at the top of all other pages
			let specialUsage = document.querySelector(".content-page-metadata").dataset.pdfusageFooter;
			let boldAndBlackFooter =
				document.querySelector(".content-page-metadata").dataset.pdfusageFooterHighlight;
			let footerStyle = "footer";
			if (boldAndBlackFooter === "true") {
				footerStyle = "disclosure-bold-black";
			}
			if (currentPage === 1) {
				return {
					margin: [0, specialUsage !== undefined && specialUsage !== "" ? 20 : 50, 0, 0],
					columns: [
						{
							width: COL_ONE_WIDTH,
							text: ""
						},
						{
							margin: [50, 0, 43, 0],
							style: "footer",
							stack: [
								{
									text:
										specialUsage !== undefined && specialUsage !== ""
											? decodeURIComponent(specialUsage.replace(/\+/g, "%20"))
											: "",
									margin: [0, 0, 0, 10],
									style: footerStyle
								},
								footerStandardDisclosure
							]
						}
					]
				};
			}
			// last page footer
			else if (currentPage === pageCount) {
				let cmax = document.querySelector(".content-page-metadata").dataset.pdfcmax;
				return {
					columns: [
						{
							alignment: "left",
							margin: [25, 20, 0, 0],
							stack: [
								{
									text: homeUrl,
									style: "home-url",
									margin: [50, -30, 0, 20]
								},
								{
									margin: [25, 0, 0, 0],
									svg: logo,
									fit: [116, 27]
								}
							]
						},
						{
							width: "auto",
							alignment: "right",
							margin: [0, 20, 50, 0],
							stack: [
								{
									text:
										cmax !== undefined && cmax !== ""
											? cmax.toUpperCase() + " | " + todaysDate
											: todaysDate,
									style: "cmax"
								},
								{
									text: pageUrlText,
									style: "page-url",
									link: pageUrl
								}
							]
						}
					]
				};
			} else {
				return { text: "", margin: [] };
			}
		},
		styles,
		defaultStyle: styles.default
	};
	// create the pdf and download
	try {
		const url = window.location.pathname;
		const pdfFilename = url.substring(url.lastIndexOf("/") + 1).toLowerCase();

		if (process.env.NODE_ENV === "development") {
			const pdfDocGenerator = pdfMake.createPdf(dd);
			return pdfDocGenerator.getDataUrl((dataUrl) => {
				const targetElement = document.querySelector("#pdf-container");
				const iframe = document.createElement("iframe");
				iframe.src = dataUrl;
				targetElement.appendChild(iframe);
			});
		}
		pdfMake.createPdf(dd).download(pdfFilename, function () {
			hidePlaceholders();
		});
	} catch (err) {
		console.error("failed: " + err);
		const closestModal = document.getElementsByClassName("content-page");
		const nodeDiv = document.createElement("div");
		nodeDiv.classList.add("body-mask-error");
		var textnode = document.createTextNode("There was a problem with your download.");
		nodeDiv.appendChild(textnode);
		closestModal[0].appendChild(nodeDiv);
		setTimeout(function () {
			hidePlaceholders();
		}, 2000);
	}
};

//remove all placeholders
const hidePlaceholders = () => {
	[...document.querySelectorAll(".body-mask-indicator")].map((bodyMask) =>
		bodyMask.classList.remove("body-mask-show")
	);
	[...document.querySelectorAll(".download-button-placeholder")].map((button) =>
		button.classList.remove("button-icon-hide")
	);
	[...document.querySelectorAll(".dot-loader-placeholder")].map((downloadButton) =>
		downloadButton.classList.add("button-icon-hide")
	);

	document.getElementsByClassName("body-mask-error")?.[0]?.remove();
};
// styling hidden footnotes
const styleHiddenFootnotes = (hiddenText) => {
	let hiddenContent = [];
	let SupCount = 1;
	let styledHidden = [];

	// Convert the javascript to an html object and loop through the nodes
	let parser = new DOMParser();

	for (let i = 0; i < hiddenText.length; i++) {
		let elHtml = parser
			.parseFromString(decodeURIComponent(hiddenText[i]).replace(/\+/g, " "), "text/html")
			.querySelector("body");

		if (elHtml.firstElementChild.nodeName !== "DIV") {
			elHtml = parser.parseFromString(
				"<div>" + decodeURIComponent(hiddenText[i]).replace(/\+/g, " ") + "</div>",
				"text/html"
			);
		}
		styledHidden.push(styleElement(elHtml.querySelector("div"), "footNote", true));
	}

	for (let j = 0; j < styledHidden.length; j++) {
		hiddenContent.push({
			columns: [
				{
					width: 20,
					text: SupCount + ". ",
					style: "footNote"
				},
				{
					width: "*",
					text: styledHidden[j]
				}
			]
		});
		SupCount++;
	}
	return hiddenContent;
};

class PDFConvertor {
	convert() {
		const cpMainContent = document.querySelector(".content-page");
		let imageDataUrls = [];
		const imageElements = document.querySelectorAll(".content-page img");

		// loop through all images on the page and give the image and its exhibit parent node an id so they can be put back together later when the images have been converted to base64
		imageElements.forEach((imageEl) => {
			if (imageEl.closest(".pdf--ignore") === null) {
				const tmpId = Math.floor(Math.random() * 99999);
				imageEl.setAttribute("data-pdf-id", tmpId);
				let imageElParent = imageEl.closest(".content-page-module-exhibit");
				let imageElParentFull = imageEl.closest(".content-page-module-exhibit-full");

				if (imageElParent !== null) {
					imageElParent.setAttribute("data-pdf-id", tmpId);
				} else if (imageElParentFull !== null) {
					imageElParentFull.setAttribute("data-pdf-id", tmpId);
					// 2024/2025: revamped image component has a picture element
				} else if (imageEl.parentElement.tagName === "PICTURE") {
					imageEl.closest(".content-page-module-image").setAttribute("data-pdf-id", tmpId);
				} else {
					imageEl.parentNode.setAttribute("data-pdf-id", tmpId);
				}
			}
		});

		const imageCount = imageElements.length;
		let count = 0;
		if (imageCount === 0) {
			walkContentNodes(cpMainContent);
		}
		for (let i = 0; i < imageCount; i++) {
			count += 1;
			/** There may be a mix of svgs/pngs within content, first we get the content-type of the image, if it's a svg we make a second request to get the svg*/
			let contentType = "";
			// eslint-disable-next-line no-loop-func
			ky(imageElements[i].src).then((response) => {
				contentType = response.headers.get("Content-Type");
				/*  Since PDFMake does not currently support svgs, the following steps must be taken:
                        1 - give the <img> tag a temp ID 
                        2 - get src of <img>
                        3 - grab the svg element from the src url from step 2 via GET
                        4 - grab width/height of svg from viewBox
                        5 - convert returned xml to a base64 string 
                        6 - use width/height from step 4 to set width/height of a canvas element that will draw an image whose src is the svg base64 string
                        7 - per <img> tag create an Image object, set its dimensions to those equal to the canvas dimensions
                        8 - once the image is loaded, draw it on the canvas, convert the canvas image to a base64 png
                        9 - calculate appropriate size the image should be in points, should also fit within page dimensions (also in points) https://github.com/bpampuch/pdfmake/blob/master/src/standardPageSizes.js
                        10 - create an object that holds: the png base64 string, the temp ID, the width/height in points. Push object into array that will be used in translatePdf() 
                    */

				let imageDisclosures = [];
				let imageDisclosuresTop = [];
				let imageDisclosuresBottom = [];

				let imageDisclosureFull = imageElements[i].closest(".content-page-module-exhibit-full");
				let imageDisclosure = imageElements[i].closest(".content-page-module-exhibit");
				if (imageDisclosureFull !== null) {
					let imageHasDisclosureFull = imageDisclosureFull.querySelector(
						".content-page-module-exhibit-full-disclosures"
					);
					if (imageHasDisclosureFull !== null) {
						imageDisclosures.push({
							text: styleElement(
								imageDisclosureFull.querySelector(".content-page-module-exhibit-full-disclosures"),
								"exhibit-disclosure",
								false
							)
						});
					}
				}
				// there are three base types of disclosure under image disclosures - risk, always visible text, and hidden popup text
				else if (imageDisclosure !== null) {
					let imageHasDisclosure = imageDisclosure.querySelector(
							".content-page-module-exhibit-disclosures"
						),
						// FIXME: top and bottom disclosure containers need to be conditional classes based on existing disclosures in the placeholder in the cshtml
						imageHasDisclosureTop = imageDisclosure.querySelector(
							".content-page-module-exhibit-disclosures-top"
						),
						imageHasDisclosureBottom = imageDisclosure.querySelector(
							".content-page-module-exhibit-disclosures-bottom"
						);

					if (
						imageHasDisclosure !== null ||
						imageHasDisclosureTop !== null ||
						imageHasDisclosureBottom !== null
					) {
						let imageDisclosureText = imageHasDisclosure?.querySelectorAll(
							".content-page-module-exhibit-disclosures .disclosure-text"
						);
						let imageDisclosuresTextTop = imageHasDisclosureTop?.querySelectorAll(
							".content-page-disclaimer"
						);
						let imageDisclosuresTextBottom = imageHasDisclosureBottom?.querySelectorAll(
							".content-page-disclaimer"
						);
						let imageIDisclosureText = imageHasDisclosure?.querySelector("[data-tip]");

						// risk and standard disclosures
						for (let i = 0; i < imageDisclosureText?.length; i++) {
							let isRisk = false;

							if (
								imageDisclosureText[i].classList.contains("risk-disclosure") ||
								imageDisclosureText[i].querySelector(".risk-disclosure") !== null
							) {
								isRisk = true;
							}

							imageDisclosures.push({
								text: styleElement(
									imageDisclosureText[i],
									isRisk ? "disclosure-image-risk" : "exhibit-disclosure",
									false
								)
							});
						}
						// top disclosures -  risk-disclosure or esma-disclosure(esma only in top container)
						for (let i = 0; i < imageDisclosuresTextTop?.length; i++) {
							let isRisk = false,
								isEsma = false;

							if (
								imageDisclosuresTextTop[i].classList.contains("risk-disclosure") ||
								imageDisclosuresTextTop[i].querySelector(".risk-disclosure") !== null
							) {
								isRisk = true;
							}
							if (
								imageDisclosuresTextTop[i].classList.contains("esma-disclosure") ||
								imageDisclosuresTextTop[i].querySelector(".esma-disclosure") !== null
							) {
								isEsma = true;
							}

							imageDisclosuresTop.push({
								text: styleElement(
									imageDisclosuresTextTop[i],
									isRisk
										? "disclosure-image-risk"
										: isEsma
											? "disclosure-esma"
											: "exhibit-disclosure",
									false,
									false
								)
							});
						}
						// bottom disclosures
						for (let i = 0; i < imageDisclosuresTextBottom?.length; i++) {
							let isRisk = false;

							if (
								imageDisclosuresTextBottom[i].classList.contains("risk-disclosure") ||
								imageDisclosuresTextBottom[i].querySelector(".risk-disclosure") !== null
							) {
								isRisk = true;
							}

							imageDisclosuresBottom.push({
								text: styleElement(
									imageDisclosuresTextBottom[i],
									isRisk ? "disclosure-image-risk" : "exhibit-disclosure",
									false,
									false
								)
							});
						}
						// Tooltip "I" icon disclosures.  Text is not rendered directly to the page and must be obtained from a data attrbiute, unescaped and turned into html nodes
						if (typeof imageIDisclosureText !== "undefined") {
							if (imageIDisclosureText !== null) {
								let parser = new DOMParser();
								let datasetHTML = parser
									.parseFromString(
										unescape(cleanText(imageIDisclosureText.dataset.tip)),
										"text/html"
									)
									.querySelector("body").childNodes;

								for (let j = 0; j < datasetHTML.length; j++) {
									imageDisclosures.push({
										text: styleElement(datasetHTML[j], "exhibit-disclosure", true)
									});
								}
							}
						}
					}
				}

				// for svg
				if (contentType !== "" && contentType === "image/svg+xml") {
					ky(imageElements[i].src, {
						type: "GET",
						headers: { "Content-Type": "text/xml" }
					})
						.then((response) => response.text())
						.then((res) => {
							const svgHolder = document.createElement("div");
							svgHolder.innerHTML = res;
							const svg = svgHolder.querySelector("svg");
							svg.setAttribute("preserveAspectRatio", "xMaxYMax meet");
							const svgViewBox = svg.getAttribute("viewBox");
							const svgSizes = svgViewBox.split(/\s+|,/);
							const svgWidth = parseInt(svgSizes[2], 10);
							const svgHeight = parseInt(svgSizes[3], 10);
							let svgData = new XMLSerializer().serializeToString(svg);

							getSVGBase64String(
								svgData,
								svgWidth * 2,
								svgHeight * 2,
								imageElements[i].dataset.pdfId
							).then((image) => {
								image.disclosure = imageDisclosures;
								image.disclosuresTop = imageDisclosuresTop;
								image.disclosuresBottom = imageDisclosuresBottom;

								imageDataUrls.push("Export image : ", image);

								count -= 1;
								if (count === 0) {
									walkContentNodes(cpMainContent, imageDataUrls);
								}
							});
						})
						.catch((err) => {
							console.error(err);
						});
				}
				// for png, jpeg or webp
				else if (
					contentType !== "" &&
					(contentType === "image/png" ||
						contentType === "image/jpeg" ||
						contentType === "image/webp")
				) {
					getPNGBase64String(
						imageElements[i].src,
						imageElements[i].naturalWidth,
						imageElements[i].naturalHeight,
						imageElements[i].dataset.pdfId
					).then((datauri) => {
						datauri.disclosure = imageDisclosures;
						datauri.disclosuresTop = imageDisclosuresTop;
						datauri.disclosuresBottom = imageDisclosuresBottom;
						imageDataUrls.push(datauri);
						count -= 1;
						if (count === 0) {
							walkContentNodes(cpMainContent, imageDataUrls);
						}
					});
				}
			});
		}
	}
}

async function getPNGBase64String(url, width, height, tmpId) {
	const base64 = await getDataUri(url);
	const pointConvertor = 0.75;
	const maxWidthPts = 400;
	const maxHeightPts = 1008;
	const paddedMaxHeightPts = 500;
	let imageWidth = width * pointConvertor;
	let imageHeight = height * pointConvertor;
	const aspectRatio = 1.603;

	// adjust image size if it is too wide or too long to fit on a page.  Images and their disclosures cannot be broken across pages
	if (imageWidth > maxWidthPts) {
		imageWidth = maxWidthPts;
		imageHeight = (height * pointConvertor) / aspectRatio;
	}
	if (imageHeight > maxHeightPts) {
		imageHeight = paddedMaxHeightPts;
		imageWidth = (width * pointConvertor) / aspectRatio;
	}

	return {
		pngDataUrl: base64,
		id: tmpId,
		fitWidth: imageWidth,
		fitHeight: imageHeight
	};
}

function getDataUri(url, width, height) {
	// Because svgs don't have instrinsic width and heights, we need to use the ones provided to us by getSvgBase64String.
	return new Promise((resolve) => {
		// Use the image constructor with the url provided
		const image = new Image();
		image.src = url;

		// We need to wait for the image to finish loading otherwise canvas will paint an empty canvas and return an empty data url.
		image.onload = function () {
			// Images do have widht and height, if those are available, it's best to use those to keep the image as high res as possible.
			width = width || image.naturalWidth;
			height = height || image.naturalHeight;

			// Create canvas workspace
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;
			canvas.backgroundColor = "rgb(255, 255, 255)";

			// Grab 2d canvas context to draw image
			const context = canvas.getContext("2d");
			context.drawImage(image, 0, 0, width, height);

			// Export data uri
			const base64 = canvas.toDataURL("image/png");
			resolve(base64);
		};
	});
}

async function getSVGBase64String(svgData, width, height, tmpId) {
	const maxWidthPts = 400,
		paddedMaxHeightPts = 500,
		pointConvertor = 0.75;
	let ratio = 0,
		imageWidth = width * pointConvertor,
		imageHeight = height * pointConvertor;

	// Convert svg data into base 64 so we can use it with canvas' drawImage()
	const base64SVG = "data:image/svg+xml;base64," + window.btoa(svgData);
	const base64 = await getDataUri(base64SVG, width, height);

	// Scale image as needed
	if (imageWidth > maxWidthPts) {
		ratio = maxWidthPts / imageWidth;
		imageHeight = imageHeight * ratio;
		imageWidth = imageWidth * ratio;
	}

	if (imageHeight > paddedMaxHeightPts) {
		ratio = paddedMaxHeightPts / imageHeight;
		imageWidth = imageWidth * ratio;
		imageHeight = imageHeight * ratio;
	}

	return {
		pngDataUrl: base64,
		id: tmpId,
		fitWidth: imageWidth,
		fitHeight: imageHeight
	};
}

export default PDFConvertor;
